import { openobserveLogs } from '@openobserve/browser-logs';
import { openobserveRum } from '@openobserve/browser-rum';
import {
  type Attributes,
  context,
  DiagConsoleLogger,
  DiagLogLevel,
  diag,
  type Meter,
  metrics,
  type Span,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { defaultResource, resourceFromAttributes } from '@opentelemetry/resources';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
  type ReadableSpan,
  SimpleSpanProcessor,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
// import { onCLS, onINP, onLCP } from 'web-vitals';
import { deriveApiUrlFromHostname } from '../config/apiConfig';
import { resolveAppHostname } from '../config/appOrigin';
import { usesProductionBackend, NOC_HOSTNAMES } from '../config/environmentHostnames';
import { decodeJWT } from '../utils/decodeJWT';
import {
  ClientMetadataLogRecordProcessor,
  ClientMetadataSpanProcessor,
} from './clientMetadataProcessors';
import { isMeetTelemetryUrl, NON_MEET_URL_IGNORE_PATTERN } from './meetDomains';

// import { isDev } from '../utils/optimizedImports';

let isInitialized = false;
let consoleForwardPatched = false;
// Request start times keyed by spanId — set in requestHook, consumed in
// applyCustomAttributesOnSpan. Using spanId (always accessible via the public
// Span API) avoids relying on ReadableSpan.startTime which is not available on
// the API-level Span proxy passed by FetchInstrumentation.
const _requestStartMs = new Map<string, number>();
// Per-span decision (keyed by spanId) on whether HTTP metrics should be
// recorded for it. Set in requestHook, consumed in applyCustomAttributesOnSpan.
// Computing it once guarantees the active-requests up/down counter stays
// balanced even if a span's URL attributes change between the two hooks.
const _spanRecordMetrics = new Map<string, boolean>();
// Stash of cloned failed responses keyed by the original Response.
//
// Why: `applyCustomAttributesOnSpan` runs *after* the application code has
// resumed from `await fetch(...)`. App code routinely consumes the body
// immediately (e.g. `await res.text()` / `res.json()`), after which any
// further `clone()` throws "Response body is already used". To capture the
// body for failed requests we must clone *the moment* fetch resolves, before
// returning the Response to the caller. We do that in a fetch wrapper
// installed by `installFetchBodyCaptureWrapper()` and look the clone up here
// from the OTel hook.
//
// WeakMap so clones are GC'd with their original Response (no leaks).
const _failedResponseCloneByOriginal = new WeakMap<Response, Response>();
let _fetchBodyCaptureWrapperInstalled = false;
let _httpClientHistogram: ReturnType<Meter['createHistogram']> | null = null;
let _httpClientRequestBodySize: ReturnType<Meter['createHistogram']> | null = null;
let _httpClientResponseBodySize: ReturnType<Meter['createHistogram']> | null = null;
let _httpClientActiveRequests: ReturnType<Meter['createUpDownCounter']> | null = null;
let _httpClientErrors: ReturnType<Meter['createCounter']> | null = null;
let _httpClientResponsesByStatusFamily: ReturnType<Meter['createCounter']> | null = null;

// biome-ignore format: one per line = easier diffing

const observerNocSite = 'observer.noc.meetings.sangoma.com';
const observerTestSite = 'observer.test.meetings.sangoma.com';

/**
 * @returns {string} Get Observer site for telemetry
 */
export function observerSiteFromHostname(hostname: string) {
  if (NOC_HOSTNAMES.includes(hostname)) {
    return observerNocSite;
  }
  return observerTestSite;
}

// SM-1243: every environment decision below keys off the origin the NG Meet
// bundle was served from. In MF mode `window.location.hostname` is the TeamHub
// host, which misses every allowlist here and sends production telemetry to the
// test observer while marking the session as dev.
const _appHostname = resolveAppHostname() ?? '';

/**
 * @returns {string} Retrieves Observer site for the current environment
 */
function getObserverSite() {
  if (_appHostname) {
    return observerSiteFromHostname(_appHostname);
  }
  return observerTestSite;
}

const observerSite = getObserverSite();

const appName = 'ngmeet-ui';
const _telemetryToken = '825874ae5b5dd761ff0cf750d223c6471c71fd16fc21df0b602aedfc';
// In dev mode, route OTLP through the rspack dev-server proxy (avoids browser CORS).
// The proxy forwards /v1/* → https://observer.../otlp/v1/* including the Authorization header.
const _isLocalDev = _appHostname === 'localhost' || _appHostname === '127.0.0.1';
const _otlpBase = _isLocalDev ? window.location.origin : `https://${observerSite}/otlp`;
const _telemetryTracesUrl = `${_otlpBase}/v1/traces`;
const _telemetryLogsUrl = `${_otlpBase}/v1/logs`;
const _telemetryMetricsUrl = `${_otlpBase}/v1/metrics`;
// Browsers only add traceparent/tracestate when the request URL matches CORS list.
const apiOriginForTracing = /^https:\/\/api\.[^/?#]+\.meetings\.sangoma\.com\b/;
// Exclude observer endpoints and localhost OTLP proxy paths from self-tracing.
// Without this, the SDK would create spans for its own export calls → infinite loop.
const observerOrigin = /^https:\/\/observer\.[^/?#]+\.meetings\.sangoma\.com\b/;
const localhostOtlp = /^http:\/\/localhost(:\d+)?\/v1\//;
// Skip self-instrumentation of the ipify request that populates the client.ip attribute.
const ipifyOrigin = /^https:\/\/api\.ipify\.org\b/;
const levelsMap = {
  log: 'INFO',
  info: 'INFO',
  warn: 'WARN',
  error: 'ERROR',
  debug: 'DEBUG',
};

const MAX_HEADER_ATTRIBUTE_LENGTH = 512;
const MAX_FAILED_RESPONSE_BODY_LOG_LENGTH = 4096;
// When this is wrongly true on production it disables the sensitive-header
// redaction below, leaking Authorization/Cookie values into failed-request spans.
const isDev = !usesProductionBackend(_appHostname);
const SENSITIVE_HEADER_NAMES = new Set(['authorization', 'cookie', 'set-cookie']);
const resource = defaultResource().merge(
  resourceFromAttributes({
    'service.name': appName,
    'service.namespace': 'frontend',
    'service.unit.name': appName,
    'deployment.environment': _appHostname || 'unknown',
  }),
);

export interface RumUser {
  id: string;
  name: string;
  email: string;
}

/**
 * Wrap `window.fetch` so that, the instant fetch resolves with a non-OK
 * response, we clone it and stash the clone in `_failedResponseCloneByOriginal`.
 * The original Response is returned to the caller untouched. This guarantees
 * the body is still readable from the clone even after the application calls
 * `res.text()` / `res.json()`.
 *
 * Must be installed before `FetchInstrumentation` (which itself wraps
 * `window.fetch`); FetchInstrumentation will then patch on top of our wrapper,
 * preserving its tracing behavior while letting us capture the body first.
 */
function installFetchBodyCaptureWrapper(hosted: boolean): void {
  if (_fetchBodyCaptureWrapperInstalled) {
    return;
  }
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await originalFetch(...args);
    // In hosted (MF) mode the wrapper sits on the shared page fetch, so restrict
    // body capture to Meet-specific responses — never clone the host's bodies.
    if (!response.ok && (!hosted || isMeetTelemetryUrl(response.url))) {
      try {
        _failedResponseCloneByOriginal.set(response, response.clone());
      } catch {
        /* clone unsupported in this context — diagnostics will note the miss */
      }
    }
    return response;
  };
  _fetchBodyCaptureWrapperInstalled = true;
}

export const onLogin = (user: RumUser): void => {
  openobserveRum.setUser({
    id: user.id,
    name: user.name,
    email: user.email,
  });
};

export const onLogout = (): void => {
  openobserveRum.clearUser();
};

/**
 * Initialize OpenTelemetry + RUM.
 *
 * @param hosted Whether NG Meet is running as a Module Federation remote inside
 *   a host (TeamHub). When hosted, instrumentation is scoped to Meet-specific
 *   domains and page-wide side effects (RUM/session replay, console forwarding,
 *   document-load) are skipped so we don't capture the host's activity.
 */
export const initTelemetry = (hosted = false): void => {
  if (isInitialized) {
    return;
  }

  try {
    // MUST run before FetchInstrumentation patches window.fetch so our wrapper
    // sits closest to the real fetch and can clone the Response before the
    // application code consumes its body.
    installFetchBodyCaptureWrapper(hosted);

    // In hosted mode, ignore every non-Meet URL so the host's fetch/XHR never
    // produce spans/metrics. The observer/localhost-OTLP exclusions MUST stay
    // first: observer.* lives under meetings.sangoma.com, so the allowlist
    // would otherwise re-include it and create a self-export loop.
    const fetchIgnoreUrls = hosted
      ? [observerOrigin, localhostOtlp, ipifyOrigin, NON_MEET_URL_IGNORE_PATTERN]
      : [observerOrigin, localhostOtlp, ipifyOrigin];
    const xhrIgnoreUrls = hosted
      ? [observerOrigin, localhostOtlp, NON_MEET_URL_IGNORE_PATTERN]
      : [observerOrigin, localhostOtlp];

    // Enable basic internal logging for troubleshooting (info level)
    diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.INFO);

    const otlpLogsExporter = new OTLPLogExporter({
      url: _telemetryLogsUrl,
      concurrencyLimit: 8,
      headers: { Authorization: `Bearer ${_telemetryToken}` },
    });

    const logsProvider = new LoggerProvider({
      resource,
      processors: [
        new ClientMetadataLogRecordProcessor(),
        new BatchLogRecordProcessor(otlpLogsExporter),
      ],
    });
    logs.setGlobalLoggerProvider(logsProvider);

    // OTLP HTTP exporter defaults to concurrencyLimit 1. SimpleSpanProcessor exports on
    // *every* span end → DocumentLoad + fetch fire many ends at once →
    // "Concurrent export limit reached". BatchSpanProcessor batches into few exports.
    const otlpExporter = new OTLPTraceExporter({
      url: _telemetryTracesUrl,
      concurrencyLimit: 8,
      headers: { Authorization: `Bearer ${_telemetryToken}` },
    });

    const spanProcessors: SpanProcessor[] = [
      new ClientMetadataSpanProcessor(),
      new BatchSpanProcessor(otlpExporter, {
        maxExportBatchSize: 64,
        scheduledDelayMillis: 500,
        exportTimeoutMillis: 15000,
        maxQueueSize: 2048,
      }),
    ];
    // Optional: console in dev only (avoids doubling export traffic in prod).
    try {
      const dev =
        typeof import.meta !== 'undefined' &&
        (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV;
      if (dev) {
        spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()));
      }
    } catch {
      /* no console exporter */
    }

    const provider = new WebTracerProvider({
      resource,
      spanProcessors,
    });

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          void provider.forceFlush();
        }
      });
    }

    provider.register({
      contextManager: new ZoneContextManager(),
      // W3C trace + baggage (meet-ui → meet-api → …); CORS must allow `baggage` on OTLP + API.
      propagator: new CompositePropagator({
        propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
      }),
    });

    // Resolve the fetch-diagnostics logger once and reuse it for every failed
    // fetch, since `logs.getLogger(...)` may allocate non-trivially per call.
    const fetchDiagnosticsLogger = getUiLogger('fetch-diagnostics');

    registerInstrumentations({
      instrumentations: [
        // DocumentLoad describes the page document. In hosted mode that is the
        // host's page, not Meet, so skip it.
        new DocumentLoadInstrumentation({ enabled: !hosted }),
        new FetchInstrumentation({
          ignoreUrls: fetchIgnoreUrls,
          propagateTraceHeaderCorsUrls: [apiOriginForTracing],
          measureRequestSize: true, // adds http.request.body.size span attr natively

          requestHook: (span, request) => {
            const method = (
              request instanceof Request ? request.method : (request.method ?? 'GET')
            ).toUpperCase();

            const spanId = span.spanContext().spanId;
            _requestStartMs.set(spanId, Date.now());

            const parsed = _spanUrl(span);
            // Defensive in hosted mode: even though ignoreUrls already drops
            // non-Meet spans before this hook runs, gate metric recording on the
            // Meet allowlist so the host's traffic can never leak into metrics.
            const shouldRecordMetrics = !hosted || (!!parsed && isMeetTelemetryUrl(parsed.href));
            _spanRecordMetrics.set(spanId, shouldRecordMetrics);

            if (!shouldRecordMetrics) {
              return;
            }

            // Request body size metric (best-effort, same types as current _bodyBytes)
            const bodySource =
              request instanceof Request ? request.body : (request as RequestInit).body;
            const bodyBytes = _bodyBytes(bodySource as BodyInit | null);
            if (bodyBytes !== null && bodyBytes > 0 && parsed) {
              _httpClientRequestBodySize?.record(bodyBytes, {
                'http.request.method': method,
                'server.address': parsed.hostname,
              });
            }
            _httpClientActiveRequests?.add(1, { 'http.request.method': method });
          },

          applyCustomAttributesOnSpan: (span, request, result) => {
            const method = (
              request instanceof Request ? request.method : (request.method ?? 'GET')
            ).toUpperCase();
            const parsed = _spanUrl(span);

            let url = '';

            if (typeof request === 'string') {
              url = request;
            } else if (request && typeof request === 'object') {
              const attributes = (span as unknown as ReadableSpan).attributes;
              const urlStr = ('url' in request && request.url) || `${attributes['http.url']}` || '';
              const urlObj = new URL(urlStr, window.location.origin);

              url = `${urlObj.hostname}${urlObj.pathname}`;
              addAuthRequestAttributes(span, request);
            }

            // ── existing span status logic ────────────────────────────────────
            if (result instanceof Response) {
              span.setAttribute('http.response.status_code', result.status);
              result.ok
                ? span.setStatus({ code: SpanStatusCode.OK })
                : span.setStatus({
                    code: SpanStatusCode.ERROR,
                    message: `HTTP ${result.status} ${result.statusText || ''}`.trim(),
                  });
              if ('ok' in result) {
                const status = (result?.status && `[${result?.status}]`) || '';
                span.updateName(`${method} ${url} ${status}`.trim());
              }
              if (!result.ok) {
                if (request && typeof request === 'object') {
                  addFailedRequestHeaderAttributes(span, request);
                }
                if (result.headers instanceof Headers) {
                  addFailedResponseHeaderAttributes(span, result.headers);
                }
                // The body clone was made by `installFetchBodyCaptureWrapper`
                // the moment fetch resolved, so it is still readable here even
                // if the application has already consumed `result`'s body.
                // If the wrapper wasn't installed (e.g. fetch unsupported) or
                // the lookup misses, fall back to a best-effort clone now.
                let bodyClone = _failedResponseCloneByOriginal.get(result) ?? null;
                _failedResponseCloneByOriginal.delete(result);
                let cloneError: string | undefined;
                if (!bodyClone) {
                  try {
                    bodyClone = result.clone();
                  } catch (error) {
                    cloneError = error instanceof Error ? error.message : String(error);
                  }
                }
                void logFailedFetchResponse(
                  fetchDiagnosticsLogger,
                  span,
                  result,
                  bodyClone,
                  cloneError,
                  method,
                  url,
                );
              }
            }

            // ── duration via wall-clock recorded in requestHook ───────────────
            const spanId = span.spanContext().spanId;
            const startMs = _requestStartMs.get(spanId);
            _requestStartMs.delete(spanId);
            const durationSec = startMs ? (Date.now() - startMs) / 1000 : 0;

            // Mirror the requestHook decision so metrics (and the balanced
            // active-requests counter) are only recorded for Meet traffic.
            const shouldRecordMetrics = _spanRecordMetrics.get(spanId) ?? !hosted;
            _spanRecordMetrics.delete(spanId);
            if (!shouldRecordMetrics) {
              return;
            }

            // ── response body size ────────────────────────────────────────────
            let responseBodyBytes = 0;
            if (result instanceof Response) {
              const cl =
                result.headers.get('x-uncompressed-content-length') ??
                result.headers.get('content-length');
              if (cl) {
                responseBodyBytes = parseInt(cl, 10) || 0;
              }
            }
            if (responseBodyBytes === 0 && parsed && typeof performance !== 'undefined') {
              const entries = performance.getEntriesByName(
                parsed.href,
                'resource',
              ) as PerformanceResourceTiming[];
              if (entries.length > 0) {
                responseBodyBytes = entries[entries.length - 1].decodedBodySize ?? 0;
              }
            }
            try {
              if (parsed) {
                const statusCode = result instanceof Response ? result.status : undefined;
                const base: Attributes = {
                  'http.request.method': method,
                  'server.address': parsed.hostname,
                };
                if (durationSec > 0) {
                  const attrs: Attributes = {
                    ...base,
                    'url.scheme': parsed.protocol.replace(':', ''),
                    'network.protocol.name': 'http',
                  };
                  if (statusCode !== undefined) {
                    attrs['http.response.status_code'] = statusCode;
                  }
                  if (parsed.port) {
                    attrs['server.port'] = parseInt(parsed.port, 10);
                  }
                  _httpClientHistogram?.record(durationSec, attrs);
                }
                if (responseBodyBytes > 0 && statusCode !== undefined) {
                  _httpClientResponseBodySize?.record(responseBodyBytes, {
                    ...base,
                    'http.response.status_code': statusCode,
                  });
                }
                if (statusCode !== undefined) {
                  const family = `${Math.floor(statusCode / 100)}xx`;
                  _httpClientResponsesByStatusFamily?.add(1, {
                    ...base,
                    'http.response.status_family': family,
                  });
                }
                if (!(result instanceof Response)) {
                  _httpClientErrors?.add(1, {
                    ...base,
                    'error.type': result instanceof Error ? result.name : 'unknown',
                  });
                }
              }
            } catch {
              /* skip */
            }

            _httpClientActiveRequests?.add(-1, { 'http.request.method': method });
          },
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: xhrIgnoreUrls,
          propagateTraceHeaderCorsUrls: [apiOriginForTracing],
        }),
      ],
    });
    initMetrics();
    // RUM (session replay, interaction/long-task/resource tracking) is
    // page-wide. In hosted mode it would record the host's page, so skip it.
    if (!hosted) {
      initRum(_appHostname);
    }
    isInitialized = true;
    diag.info('OpenTelemetry WebTracerProvider initialized');

    // Console is a shared global; in hosted mode we cannot separate Meet's logs
    // from the host's, so we skip console forwarding entirely when hosted.
    // OTel spans/metrics/logs still flow.
    if (!hosted) {
      // Patch console AFTER full initialization so that any OTEL-internal diag
      // calls during setup don't recurse through the patched methods.
      const browserConsoleLogger = getUiLogger('browser-console');
      patchConsole(browserConsoleLogger);
    }
  } catch (error) {
    // Do not crash the app if telemetry fails to init
    // eslint-disable-next-line no-console
    console.error('Failed to initialize OpenTelemetry:', error);
  }
};

export const getUiTracer = () => {
  return trace.getTracer(appName, '1.0.0');
};

export const getUiLogger = (name: string) => logs.getLogger(`${appName}.${name}`, '1.0.0');
export const initRum = (site: string): void => {
  if (NOC_HOSTNAMES.includes(site)) {
    initRumForProduction();
  } else {
    initRumForTest();
  }
};
export const initRumForTest = (): void => {
  const options = {
    clientToken: 'rumGhLk0Hge7fIGPsuO',
    applicationId: appName,
    site: observerSite,
    service: appName,
    env: 'test',
    version: '0.0.1',
    organizationIdentifier: 'default',
    insecureHTTP: false,
    apiVersion: 'v1',
  };

  openobserveRum.init({
    applicationId: options.applicationId,
    clientToken: options.clientToken,
    site: options.site,
    organizationIdentifier: options.organizationIdentifier,
    service: options.service,
    env: options.env,
    version: options.version,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    apiVersion: options.apiVersion,
    insecureHTTP: options.insecureHTTP,
    defaultPrivacyLevel: 'allow', // 'allow' or 'mask-user-input' or 'mask'. Use one of the 3 values.
    // Enables end-to-end trace correlation from RUM to backend services by injecting tracing headers into matched outgoing requests.
    allowedTracingUrls: [
      {
        match: deriveApiUrlFromHostname(window.location.hostname),
        propagatorTypes: ['openobserve', 'tracecontext'],
      },
    ],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 50,
  });

  openobserveLogs.init({
    clientToken: options.clientToken,
    site: options.site,
    organizationIdentifier: options.organizationIdentifier,
    service: options.service,
    env: options.env,
    version: options.version,
    forwardErrorsToLogs: true,
    insecureHTTP: options.insecureHTTP,
    apiVersion: options.apiVersion,
  });

  openobserveRum.startSessionReplayRecording();
};
export const initRumForProduction = (): void => {
  const options = {
    clientToken: 'rumFle85nNto6HrX59F',
    applicationId: 'web-application-id',
    site: observerSite,
    service: appName,
    env: 'production',
    version: '0.0.1',
    organizationIdentifier: 'default',
    insecureHTTP: false,
    apiVersion: 'v1',
  };
  
  openobserveRum.init({
    applicationId: options.applicationId, // required, any string identifying your application
    clientToken: options.clientToken,
    site: options.site,
    organizationIdentifier: options.organizationIdentifier,
    service: options.service,
    env: options.env,
    version: options.version,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    apiVersion: options.apiVersion,
    insecureHTTP: options.insecureHTTP,
    defaultPrivacyLevel: 'allow', // 'allow' or 'mask-user-input' or 'mask'. Use one of the 3 values.
    // Enables end-to-end trace correlation from RUM to backend services by injecting tracing headers into matched outgoing requests.
    allowedTracingUrls: [
      {
        match: deriveApiUrlFromHostname(window.location.hostname),
        propagatorTypes: ['openobserve', 'tracecontext'], // Header formats: 'openobserve' for x-openobserve-* headers, 'tracecontext' for W3C traceparent
      },
    ],
    sessionSampleRate: 100, // Track 100% of sessions
    sessionReplaySampleRate: 50, // Record 50% of sessions
  });
  
  openobserveLogs.init({
    clientToken: options.clientToken,
    site: options.site,
    organizationIdentifier: options.organizationIdentifier,
    service: options.service,
    env: options.env,
    version: options.version,
    forwardErrorsToLogs: true,
    insecureHTTP: options.insecureHTTP,
    apiVersion: options.apiVersion,
  });
  
  openobserveRum.startSessionReplayRecording();

};
function addAuthRequestAttributes(span: Span, request: Request | RequestInit): void {
  const headers = request instanceof Request ? request.headers : request.headers;
  const authorization = readHeader(headers, 'authorization');

  span.setAttribute('http.request.header.authorization_present', !!authorization);
  span.setAttribute('http.request.header.cookie_present', !!readHeader(headers, 'cookie'));
  addAuthorizationTokenAttributes(span, authorization);
}

function addFailedRequestHeaderAttributes(span: Span, request: Request | RequestInit): void {
  const headers = request instanceof Request ? request.headers : request.headers;

  for (const [name, value] of readHeaders(headers)) {
    const normalizedName = normalizeHeaderName(name);

    if (!isDev && SENSITIVE_HEADER_NAMES.has(name.toLowerCase())) {
      span.setAttribute(`http.request.header.${normalizedName}_present`, true);
      continue;
    }

    span.setAttribute(
      `http.request.header.${normalizedName}`,
      truncate(value, MAX_HEADER_ATTRIBUTE_LENGTH),
    );
  }

  if (isDev) {
    span.setAttribute('security.warning.failed_request_headers_captured_for_debug', true);
  }
}

function addFailedResponseHeaderAttributes(span: Span, headers: Headers): void {
  for (const [name, value] of headers.entries()) {
    span.setAttribute(
      `http.response.header.${normalizeHeaderName(name)}`,
      truncate(value, MAX_HEADER_ATTRIBUTE_LENGTH),
    );
  }

  span.setAttribute('http.response.header.set_cookie_present', headers.has('set-cookie'));
}

/**
 * Emit a log record with the failed response body (and any read error) so
 * operators can diagnose server-side failures, and mirror the same fields as
 * span attributes so the body is visible directly on the failing span.
 *
 * The body must be cloned synchronously by the caller (see
 * `applyCustomAttributesOnSpan`) because by the time this async helper runs,
 * application code will typically have already consumed the original
 * `Response`'s body, after which `clone()` throws "Response body is already
 * used". `span.setAttribute` on an already-ended span is a safe no-op, so the
 * span fields are best-effort: they will land on the span if it is still
 * recording when the body finishes reading (the common case for typical
 * response sizes).
 */
async function logFailedFetchResponse(
  logger: ReturnType<typeof logs.getLogger>,
  span: Span,
  response: Response,
  bodyClone: Response | null,
  cloneError: string | undefined,
  method: string,
  url: string,
): Promise<void> {
  let body = '';
  let readError: string | undefined = cloneError;

  if (bodyClone) {
    try {
      body = await bodyClone.text();
    } catch (error) {
      readError = error instanceof Error ? error.message : String(error);
    }
  }

  const truncatedBody = truncate(body, MAX_FAILED_RESPONSE_BODY_LOG_LENGTH);
  const wasTruncated = body.length > MAX_FAILED_RESPONSE_BODY_LOG_LENGTH;

  span.setAttribute('http.response.body', truncatedBody);
  span.setAttribute('http.response.body.length', body.length);
  span.setAttribute('http.response.body.truncated', wasTruncated);
  if (readError) {
    span.setAttribute('http.response.body.read_error', readError);
  }

  logger.emit({
    severityText: 'WARN',
    attributes: {
      'body.message': `Failed fetch response: ${response.status} ${method} ${url}`,
      'http.url': url,
      'http.request.method': method,
      'http.response.status_code': response.status,
      'http.response.body': truncatedBody,
      'http.response.body.length': body.length,
      'http.response.body.truncated': wasTruncated,
      ...(readError ? { 'http.response.body.read_error': readError } : {}),
    },
  });
}

function readHeader(headers: HeadersInit | undefined, name: string): string | null {
  if (!headers) {
    return null;
  }

  const normalizedName = name.toLowerCase();

  if (headers instanceof Headers) {
    return headers.get(name);
  }

  if (Array.isArray(headers)) {
    const found = headers.find(([key]) => key.toLowerCase() === normalizedName);
    return found?.[1] ?? null;
  }

  const record = headers as Record<string, string>;
  return record[name] ?? record[normalizedName] ?? null;
}

function readHeaders(headers: HeadersInit | undefined): Array<[string, string]> {
  if (!headers) {
    return [];
  }

  if (headers instanceof Headers) {
    return Array.from(headers.entries());
  }

  if (Array.isArray(headers)) {
    return headers;
  }

  return Object.entries(headers);
}

function addAuthorizationTokenAttributes(span: Span, authorization: string | null): void {
  const accessToken = authorization?.match(/^Bearer\s+(.+)$/i)?.[1];
  if (!accessToken) {
    return;
  }

  let payload: Record<string, unknown>;
  try {
    payload = decodeJWT(accessToken);
  } catch {
    return;
  }

  if (typeof payload?.exp !== 'number') {
    return;
  }

  const secondsUntilExpiration = payload.exp - Math.floor(Date.now() / 1000);
  span.setAttribute('auth.access_token.exp', payload.exp);
  span.setAttribute('auth.access_token.expires_at', new Date(payload.exp * 1000).toISOString());
  span.setAttribute('auth.access_token.seconds_until_expiration', secondsUntilExpiration);
}

function normalizeHeaderName(name: string): string {
  return name.toLowerCase().replaceAll('-', '_');
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}

/** Return byte length of a fetch body, or null when it cannot be determined cheaply. */
function _bodyBytes(body: BodyInit | ReadableStream | null | undefined): number | null {
  if (body === null || body === undefined) {
    return 0;
  }
  if (typeof body === 'string') {
    return new TextEncoder().encode(body).byteLength;
  }
  if (body instanceof ArrayBuffer) {
    return body.byteLength;
  }
  if (ArrayBuffer.isView(body)) {
    return body.byteLength;
  }
  if (body instanceof Blob) {
    return body.size;
  }
  if (body instanceof URLSearchParams) {
    return new TextEncoder().encode(body.toString()).byteLength;
  }
  return null; // FormData / ReadableStream — size not cheaply knowable
}

function _spanUrl(span: Span): URL | null {
  const attrs = (span as unknown as { attributes?: Record<string, unknown> }).attributes;
  const raw = attrs?.['url.full'] ?? attrs?.['http.url'];
  if (typeof raw !== 'string' || !raw) {
    return null;
  }
  try {
    return new URL(raw, location.href);
  } catch {
    return null;
  }
}

export const initMetrics = (): void => {
  const exporter = new OTLPMetricExporter({
    url: _telemetryMetricsUrl,
    concurrencyLimit: 8,
    headers: { Authorization: `Bearer ${_telemetryToken}` },
  });

  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter,
        exportIntervalMillis: 5000,
      }),
    ],
  });
  metrics.setGlobalMeterProvider(meterProvider);
  const meter = meterProvider.getMeter('ngmeet-ui');
  _httpClientHistogram = meter.createHistogram('http.client.request.duration', {
    unit: 's',
    description: 'Duration of outbound HTTP requests',
  });
  _httpClientRequestBodySize = meter.createHistogram('http.client.request.body.size', {
    unit: 'By',
    description: 'Size of outbound HTTP request bodies',
  });
  _httpClientResponseBodySize = meter.createHistogram('http.client.response.body.size', {
    unit: 'By',
    description:
      'Size of inbound HTTP response bodies (from X-Uncompressed-Content-Length / Content-Length response header)',
  });
  _httpClientActiveRequests = meter.createUpDownCounter('http.client.active_requests', {
    unit: '{request}',
    description: 'Number of in-flight outbound HTTP requests',
  });
  _httpClientErrors = meter.createCounter('http.client.request.errors', {
    unit: '{error}',
    description: 'Number of outbound HTTP requests that failed at the network level',
  });
  _httpClientResponsesByStatusFamily = meter.createCounter(
    'http.client.responses_by_status_family',
    {
      unit: '{response}',
      description: 'Count of HTTP responses grouped by status code family (2xx, 3xx, 4xx, 5xx)',
    },
  );

  // ── Web Vitals (kept for reference — currently handled by OpenObserve RUM) ──
  // OpenObserve RUM captures LCP/CLS/INP via its own PerformanceObserver.
  // Uncomment below (and import { onCLS, onINP, onLCP } from 'web-vitals') to
  // switch to OTel-native web vitals if RUM is ever removed.
  //
  // const connectionType =
  //   (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
  //     ?.effectiveType ?? 'unknown';
  // const vitalsAttrs = (m: { rating: string }) => ({
  //   url: location.pathname,
  //   rating: m.rating,
  //   connection_effective_type: connectionType,
  // });
  // const vitalsMeter = meterProvider.getMeter('web-vitals');
  // const lcp = vitalsMeter.createHistogram('web_vitals_lcp', { unit: 'ms' });
  // const cls = vitalsMeter.createHistogram('web_vitals_cls');
  // const inp = vitalsMeter.createHistogram('web_vitals_inp', { unit: 'ms' });
  // onLCP((m) => lcp.record(m.value, vitalsAttrs(m)));
  // onCLS((m) => cls.record(m.value, vitalsAttrs(m)));
  // onINP((m) => inp.record(m.value, vitalsAttrs(m)));
};

function patchConsole(browserLogger: ReturnType<typeof logs.getLogger>): void {
  if (consoleForwardPatched) {
    return;
  }
  consoleForwardPatched = true;

  // Guard against re-entrant calls: OTEL internals (DiagConsoleLogger) call
  // console.* which would re-enter this patch and cause a stack overflow.
  let forwarding = false;

  Object.keys(levelsMap).forEach((method) => {
    const key = method as keyof typeof levelsMap;
    const original = console[key] as (...args: unknown[]) => void;

    console[key] = ((...args: unknown[]) => {
      original.apply(console, args);

      if (forwarding) {
        return;
      }
      forwarding = true;
      try {
        const span = trace.getSpan(context.active());
        const spanCtx = span?.spanContext();
        const attributes = {
          'body.message': args
            .map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a)))
            .join(' '),
          'console.method': key,
          ...(spanCtx
            ? {
                trace_id: spanCtx.traceId,
                span_id: spanCtx.spanId,
              }
            : {}),
        };
        browserLogger.emit({
          severityText: levelsMap[key],
          attributes,
        });
      } finally {
        forwarding = false;
      }
    }) as (typeof console)[typeof key];
  });
}
